import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { TabsModule } from 'primeng/tabs';
import { ImageModule } from 'primeng/image';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FileUploadModule, FileSelectEvent } from 'primeng/fileupload';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ProjectService } from '../../services/project.service';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { CompanyService } from '../../services/company.service';
import { Company } from 'src/app/user/model/user';
import {
  Project,
  CreateProjectDTO,
  UpdateProjectDTO,
  ProjectStatus,
  ProjectScope,
  ProjectType,
  Region,
  CertificationStandard,
  CoBenefit,
  PROJECT_TYPE_IMPACT_METRICS,
  PartnerCompanyConfig,
  OdooProduct,
} from '../../models/project.model';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DialogModule } from 'primeng/dialog';
import { forkJoin } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-edit-project',
  imports: [
    CommonModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    DatePickerModule,
    TabsModule,
    ImageModule,
    ProgressSpinnerModule,
    ToastModule,
    ConfirmDialogModule,
    FileUploadModule,
    ReactiveFormsModule,
    FormsModule,
    TooltipModule,
    DialogModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './edit-project.component.html',
  styleUrl: './edit-project.component.scss',
})
export class EditProjectComponent implements OnInit {
  fb = inject(FormBuilder);
  projectService = inject(ProjectService);
  companyService = inject(CompanyService);
  activeRoute = inject(ActivatedRoute);
  router = inject(Router);
  confirmationService = inject(ConfirmationService);
  notificationService = inject(NotificationService);
  breadcrumbService = inject(BreadcrumbService);
  location = inject(Location);
  sanitizer = inject(DomSanitizer);

  project?: Project;
  editMode = false;
  today = new Date();
  isDeleting = false;

  // Image upload state
  mainImageUrl: string | null = null;
  certificateImageUrl: string | null = null;
  socialImageUrl: string | null = null;
  uploadingMainImage = false;
  uploadingCertificateImage = false;
  uploadingSocialImage = false;

  // Dropdown options
  statusOptions = Object.values(ProjectStatus).map((status) => ({ label: status, value: status }));
  scopeOptions = Object.values(ProjectScope).map((scope) => ({ label: scope, value: scope }));
  typeOptions = Object.values(ProjectType).map((type) => ({ label: type.replace(/_/g, ' '), value: type }));
  regionOptions = Object.values(Region).map((region) => ({ label: region.replace(/_/g, ' '), value: region }));
  certificationOptions = Object.values(CertificationStandard).map((cert) => ({ label: cert.replace(/_/g, ' '), value: cert }));
  coBenefitOptions = Object.values(CoBenefit).map((benefit) => ({ label: benefit.replace(/_/g, ' '), value: benefit }));

  // Certificate configuration options
  projectIconOptions: { label: string; value: string }[] = [];
  equivalenceOptions: { label: string; value: string }[] = [];
  certificateOptionsLoaded = false;
  showCertificatePreview = false;
  certificatePreviewUrl: SafeResourceUrl | null = null;

  // Partner companies (project restrictions)
  partnerCompanies: PartnerCompanyConfig[] = [];
  allCompanies: Company[] = [];
  selectedCompanyIds: string[] = [];
  loadingCompanies = false;
  loadingPartnerCompanies = false;

  // Odoo products
  odooProducts: OdooProduct[] = [];
  odooProductOptions: { label: string; value: number }[] = [];
  loadingOdooProducts = false;

  // Reactive form
  projectForm = this.fb.group({
    // Basic info
    slug: [{ value: '', disabled: true }],
    status: [ProjectStatus.DRAFT],
    isVisible: [true],
    isDefault: [false],
    isFeatured: [false],

    // Multilingual content (EN)
    nameEN: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
    shortDescriptionEN: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]],
    longDescriptionEN: ['', [Validators.required, Validators.minLength(50)]],
    certificateTextEN: [''],
    socialTextEN: [''],

    // Multilingual content (FR)
    nameFR: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
    shortDescriptionFR: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]],
    longDescriptionFR: ['', [Validators.required, Validators.minLength(50)]],
    certificateTextFR: [''],
    socialTextFR: [''],

    // Multilingual content (NL)
    nameNL: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
    shortDescriptionNL: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]],
    longDescriptionNL: ['', [Validators.required, Validators.minLength(50)]],
    certificateTextNL: [''],
    socialTextNL: [''],

    // Classification
    projectType: [null as ProjectType | null, Validators.required],
    projectScope: [ProjectScope.GLOBAL, Validators.required],
    region: [null as Region | null, Validators.required],
    location: [''],
    country: [''],

    // Pricing
    pricePerTonne: [0, [Validators.required, Validators.min(0)]],

    // Certification
    certificationStandard: [null as CertificationStandard | null, Validators.required],
    certificationYear: [null as number | null],
    isCertifiedOffset: [false],

    // Impact metrics (dynamic based on projectType)
    savedTreesPerTonne: [null as number | null],
    protectedAresPerTonne: [null as number | null],
    cleanWaterLitersPerTonne: [null as number | null],
    peopleCleanWaterPerTonne: [null as number | null],
    cookstovesPerTonne: [null as number | null],
    householdsSuppliedPerTonne: [null as number | null],
    annualCO2Reduction: [null as number | null],
    annualBeneficiaries: [null as number | null],

    // Co-benefits
    coBenefits: [[] as CoBenefit[], Validators.required],

    // Certificate configuration
    projectIcons: [[] as string[]],
    certificateEquivalences: [[] as string[]],

    // Dates
    startDate: [null as Date | null],
    expirationDate: [null as Date | null],

    // Odoo integration
    odooProductId: [null as number | null],
  });

  ngOnInit(): void {
    this.setMode();
    this.loadCertificateOptions();
    this.loadAllCompanies();
    this.loadOdooProducts();
    this.initializeForm();
    this.setupSlugGeneration();
    this.setupProjectTypeWatcher();
  }

  loadAllCompanies(): void {
    this.loadingCompanies = true;
    this.companyService.getCompanyList().subscribe({
      next: (companies) => {
        this.allCompanies = companies;
        this.loadingCompanies = false;
      },
      error: (err) => {
        console.error('Error loading companies:', err);
        this.loadingCompanies = false;
      },
    });
  }

  loadOdooProducts(): void {
    this.loadingOdooProducts = true;
    this.projectService.getOdooProducts().subscribe({
      next: (response) => {
        this.odooProducts = response.products;
        this.odooProductOptions = response.products.map((p) => ({
          label: p.code ? `${p.code} - ${p.name}` : p.name,
          value: p.id,
        }));
        this.loadingOdooProducts = false;
      },
      error: (err) => {
        console.error('Error loading Odoo products:', err);
        this.loadingOdooProducts = false;
      },
    });
  }

  loadCertificateOptions(): void {
    forkJoin({
      icons: this.projectService.getProjectIcons(),
      equivalences: this.projectService.getEquivalences(),
    }).subscribe({
      next: ({ icons, equivalences }) => {
        this.projectIconOptions = icons.data.map((icon) => ({
          label: `${icon.id} - ${icon.labelEN}`,
          value: icon.id,
        }));
        this.equivalenceOptions = equivalences.data.map((eq) => ({
          label: `${eq.icon} ${eq.labelEN}`,
          value: eq.slug,
        }));
        this.certificateOptionsLoaded = true;

        // Re-apply certificate config values after options are loaded (fixes race condition)
        this.applyCertificateConfigToForm();
      },
      error: (err) => {
        console.error('Error loading certificate options:', err);
        this.notificationService.error('Failed to load certificate configuration options');
      },
    });
  }

  private applyCertificateConfigToForm(): void {
    if (this.project && this.certificateOptionsLoaded) {
      const projectIcons = this.project.projectIcons
        ? this.project.projectIcons.split(',').map((s) => s.trim())
        : [];
      const certificateEquivalences = this.project.certificateEquivalences
        ? this.project.certificateEquivalences.split(',').map((s) => s.trim())
        : [];
      this.projectForm.patchValue({ projectIcons, certificateEquivalences });
    }
  }

  setMode(): void {
    const id = this.activeRoute.snapshot.params['id'];
    if (id) {
      this.editMode = true;
    }
  }

  initializeForm(): void {
    const id = this.activeRoute.snapshot.params['id'];
    if (id) {
      this.projectService.getProjectById(id).subscribe({
        next: (response) => {
          this.project = response.data;

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { projectIcons, certificateEquivalences, ...projectData } = this.project;
          this.projectForm.patchValue({
            ...projectData,
            startDate: this.project.startDate ? new Date(this.project.startDate) : null,
            expirationDate: this.project.expirationDate ? new Date(this.project.expirationDate) : null,
          });

          // Apply certificate config (handles race condition with options loading)
          this.applyCertificateConfigToForm();

          // Load existing image URLs
          this.mainImageUrl = this.project.mainImageUrl || null;
          this.certificateImageUrl = this.project.certificateImageUrl || null;
          this.socialImageUrl = this.project.socialShareImageUrl || null;

          // Set breadcrumb label
          this.breadcrumbService.setLabel(this.project.id, this.project.nameEN);

          // Load partner companies
          this.loadPartnerCompanies();
        },
        error: (err) => {
          console.error('Error fetching project:', err);
          this.notificationService.error('Failed to load project');
        },
      });
    }
  }

  setupSlugGeneration(): void {
    // Auto-generate slug from nameEN (only in create mode)
    if (!this.editMode) {
      this.projectForm.get('nameEN')?.valueChanges.subscribe((name) => {
        if (name) {
          const slug = name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
          this.projectForm.patchValue({ slug }, { emitEvent: false });
        }
      });
    }
  }

  setupProjectTypeWatcher(): void {
    this.projectForm.get('projectType')?.valueChanges.subscribe((newType) => {
      if (newType && this.project) {
        // Check if there are filled metrics that will be hidden
        const currentType = this.project.projectType;
        if (currentType !== newType) {
          this.handleProjectTypeChange(currentType, newType);
        }
      }
    });
  }

  handleProjectTypeChange(oldType: ProjectType, newType: ProjectType): void {
    const oldMetrics = PROJECT_TYPE_IMPACT_METRICS[oldType] || [];
    const newMetrics = PROJECT_TYPE_IMPACT_METRICS[newType] || [];

    // Find metrics that will be hidden
    const metricsToHide = oldMetrics.filter((metric) => !newMetrics.includes(metric));

    // Check if any of these metrics have values
    const filledMetrics = metricsToHide.filter((metric) => {
      const value = this.projectForm.get(metric)?.value;
      return value !== null && value !== undefined && value !== '';
    });

    if (filledMetrics.length > 0) {
      this.confirmationService.confirm({
        message: `Changing project type will clear ${filledMetrics.length} impact metric(s): ${filledMetrics.join(', ')}. Continue?`,
        header: 'Warning',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          // Clear hidden metrics
          metricsToHide.forEach((metric) => {
            this.projectForm.patchValue({ [metric]: null });
          });
        },
        reject: () => {
          // Revert project type change
          this.projectForm.patchValue({ projectType: oldType }, { emitEvent: false });
        },
      });
    }
  }

  getVisibleImpactMetrics(): string[] {
    const projectType = this.projectForm.get('projectType')?.value;
    if (!projectType) return [];
    return PROJECT_TYPE_IMPACT_METRICS[projectType] || [];
  }

  isMetricVisible(metric: string): boolean {
    return this.getVisibleImpactMetrics().includes(metric);
  }

  getStatusClasses(status: ProjectStatus | null | undefined): string {
    if (!status) return 'bg-greyscale-100 text-greyscale-700';
    const classes: Record<ProjectStatus, string> = {
      [ProjectStatus.ACTIVE]: 'bg-validation-100 text-validation-700',
      [ProjectStatus.DRAFT]: 'bg-greyscale-100 text-greyscale-700',
      [ProjectStatus.INACTIVE]: 'bg-warning-100 text-warning-700',
      [ProjectStatus.EXPIRED]: 'bg-error-100 text-error-700',
    };
    return classes[status] || 'bg-greyscale-100 text-greyscale-700';
  }

  onSubmit(): void {
    // Check if isDefault is true and warn user
    if (this.projectForm.value.isDefault && !this.project?.isDefault) {
      this.confirmationService.confirm({
        message: 'Setting this as default will remove default status from other projects. Continue?',
        header: 'Confirmation',
        icon: 'pi pi-info-circle',
        accept: () => this.saveProject(),
      });
    } else {
      this.saveProject();
    }
  }

  private cleanFormData<T extends Record<string, unknown>>(data: T): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(data)
        .filter(([, value]) => value !== null && value !== '')
        .map(([key, value]) => [key, value])
    );
  }

  saveProject(): void {
    const formValue = this.projectForm.getRawValue();

    // Convert arrays to comma-separated strings for backend
    const projectIcons = formValue.projectIcons?.length ? formValue.projectIcons.join(',') : undefined;
    const certificateEquivalences = formValue.certificateEquivalences?.length ? formValue.certificateEquivalences.join(',') : undefined;

    if (this.editMode && this.project) {
      // Update existing project
      const updateData: UpdateProjectDTO = {
        ...this.cleanFormData(formValue),
        projectIcons,
        certificateEquivalences,
        startDate: formValue.startDate ? this.formatDateForBackend(formValue.startDate) : undefined,
        expirationDate: formValue.expirationDate ? this.formatDateForBackend(formValue.expirationDate) : undefined,
      };

      this.projectService.updateProject(this.project.id, updateData).subscribe({
        next: () => {
          this.notificationService.success('Project updated successfully');
          this.router.navigate(['/admin/projects']);
        },
        error: (err) => {
          console.error('Error updating project:', err);
          this.notificationService.error('Failed to update project');
        },
      });
    } else {
      // Create new project
      const cleanedData = this.cleanFormData(formValue);
      const createData: CreateProjectDTO = {
        ...cleanedData,
        slug: formValue.slug!,
        nameEN: formValue.nameEN!,
        nameFR: formValue.nameFR!,
        nameNL: formValue.nameNL!,
        shortDescriptionEN: formValue.shortDescriptionEN!,
        shortDescriptionFR: formValue.shortDescriptionFR!,
        shortDescriptionNL: formValue.shortDescriptionNL!,
        longDescriptionEN: formValue.longDescriptionEN!,
        longDescriptionFR: formValue.longDescriptionFR!,
        longDescriptionNL: formValue.longDescriptionNL!,
        projectType: formValue.projectType!,
        projectScope: formValue.projectScope!,
        region: formValue.region!,
        pricePerTonne: formValue.pricePerTonne!,
        certificationStandard: formValue.certificationStandard!,
        isCertifiedOffset: formValue.isCertifiedOffset!,
        coBenefits: formValue.coBenefits!,
        projectIcons,
        certificateEquivalences,
        startDate: formValue.startDate ? this.formatDateForBackend(formValue.startDate) : undefined,
        expirationDate: formValue.expirationDate ? this.formatDateForBackend(formValue.expirationDate) : undefined,
      };

      this.projectService.createProject(createData).subscribe({
        next: (response) => {
          this.notificationService.success('Project created successfully');
          this.router.navigate(['/admin/projects/edit', response.data.id]);
        },
        error: (err) => {
          console.error('Error creating project:', err);
          this.notificationService.error('Failed to create project');
        },
      });
    }
  }

  onCancel(): void {
    this.location.back();
  }

  backToList(): void {
    this.router.navigate(['/admin/projects']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private formatDateForBackend(date: Date): string {
    // VineJS date validator expects YYYY-MM-DD format, not full ISO string
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Image upload methods
  onMainImageSelect(event: FileSelectEvent): void {
    const file = event.files?.[0];
    if (!file) return;

    if (!this.project?.id) {
      this.notificationService.error('Please save the project before uploading images');
      return;
    }

    this.uploadingMainImage = true;
    this.projectService.uploadImage(this.project.id, file, 'main').subscribe({
      next: (response) => {
        this.mainImageUrl = response.data.url;
        this.notificationService.success('Main image uploaded successfully');
        this.uploadingMainImage = false;
      },
      error: (err) => {
        console.error('Error uploading main image:', err);
        this.notificationService.error('Failed to upload main image');
        this.uploadingMainImage = false;
      },
    });
  }

  onCertificateImageSelect(event: FileSelectEvent): void {
    const file = event.files?.[0];
    if (!file) return;

    if (!this.project?.id) {
      this.notificationService.error('Please save the project before uploading images');
      return;
    }

    this.uploadingCertificateImage = true;
    this.projectService.uploadImage(this.project.id, file, 'certificate').subscribe({
      next: (response) => {
        this.certificateImageUrl = response.data.url;
        this.notificationService.success('Certificate image uploaded successfully');
        this.uploadingCertificateImage = false;
      },
      error: (err) => {
        console.error('Error uploading certificate image:', err);
        this.notificationService.error('Failed to upload certificate image');
        this.uploadingCertificateImage = false;
      },
    });
  }

  onSocialImageSelect(event: FileSelectEvent): void {
    const file = event.files?.[0];
    if (!file) return;

    if (!this.project?.id) {
      this.notificationService.error('Please save the project before uploading images');
      return;
    }

    this.uploadingSocialImage = true;
    this.projectService.uploadImage(this.project.id, file, 'social').subscribe({
      next: (response) => {
        this.socialImageUrl = response.data.url;
        this.notificationService.success('Social image uploaded successfully');
        this.uploadingSocialImage = false;
      },
      error: (err) => {
        console.error('Error uploading social image:', err);
        this.notificationService.error('Failed to upload social image');
        this.uploadingSocialImage = false;
      },
    });
  }

  // Certificate preview
  openCertificatePreview(): void {
    const projectName = this.projectForm.get('nameEN')?.value || 'Sample Project';
    const params = new URLSearchParams({
      co2Tonnes: '5',
      recipientName: 'John Doe',
      locale: 'EN',
    });

    // If editing existing project, pass projectId to load real icons/equivalences
    if (this.project?.id) {
      params.set('projectId', this.project.id);
    } else {
      params.set('projectName', projectName);
    }

    const url = `${environment.apiUrl}/certificate-preview?${params.toString()}`;
    this.certificatePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.showCertificatePreview = true;
  }

  confirmDelete(): void {
    if (!this.project?.id) return;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${this.project.nameEN}"? This action cannot be undone.`,
      header: 'Delete Project',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.deleteProject();
      },
    });
  }

  deleteProject(): void {
    if (!this.project?.id) return;

    this.isDeleting = true;
    this.projectService.deleteProject(this.project.id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.notificationService.success('Project deleted successfully');
        this.router.navigate(['/admin/projects']);
      },
      error: (err) => {
        this.isDeleting = false;
        console.error('Error deleting project:', err);
        this.notificationService.error(err.error?.message || 'Failed to delete project');
      },
    });
  }

  // ============================================
  // Partner Companies Management
  // ============================================

  loadPartnerCompanies(): void {
    if (!this.project?.id) return;

    this.loadingPartnerCompanies = true;
    this.projectService.getPartnerCompanies(this.project.id).subscribe({
      next: (response) => {
        this.partnerCompanies = response.data;
        this.selectedCompanyIds = this.partnerCompanies.map((pc) => pc.companyId);
        this.loadingPartnerCompanies = false;
      },
      error: (err) => {
        console.error('Error loading partner companies:', err);
        this.loadingPartnerCompanies = false;
      },
    });
  }

  onCompanySelectionChange(newSelectedIds: string[]): void {
    if (!this.project?.id) return;

    const currentIds = this.partnerCompanies.map((pc) => pc.companyId);
    const addedIds = newSelectedIds.filter((id) => !currentIds.includes(id));
    const removedIds = currentIds.filter((id) => !newSelectedIds.includes(id));

    // Add new companies
    addedIds.forEach((companyId) => {
      this.projectService
        .addPartnerCompany(this.project!.id, {
          companyId,
          isVisible: true,
          isDefault: false,
        })
        .subscribe({
          next: (response) => {
            this.partnerCompanies.push(response.data);
          },
          error: (err) => {
            console.error('Error adding partner company:', err);
            // Revert selection on error
            this.selectedCompanyIds = this.selectedCompanyIds.filter((id) => id !== companyId);
            this.notificationService.error('Failed to add company');
          },
        });
    });

    // Remove companies
    removedIds.forEach((companyId) => {
      this.projectService.removePartnerCompany(this.project!.id, companyId).subscribe({
        next: () => {
          this.partnerCompanies = this.partnerCompanies.filter((pc) => pc.companyId !== companyId);
        },
        error: (err) => {
          console.error('Error removing partner company:', err);
          // Revert selection on error
          this.selectedCompanyIds = [...this.selectedCompanyIds, companyId];
          this.notificationService.error('Failed to remove company');
        },
      });
    });
  }

  updatePartnerCompany(
    partner: PartnerCompanyConfig,
    field: 'pricePerTonne' | 'isDefault',
    value: number | boolean | null
  ): void {
    if (!this.project?.id) return;

    // Save previous value for rollback on error
    const previousValue = partner[field];

    this.projectService
      .updatePartnerCompany(this.project.id, partner.companyId, {
        [field]: value,
      })
      .subscribe({
        next: (response) => {
          const index = this.partnerCompanies.findIndex(
            (pc) => pc.id === partner.id
          );
          if (index !== -1) {
            this.partnerCompanies[index] = response.data;
          }
        },
        error: (err) => {
          console.error('Error updating partner company:', err);
          // Restore previous value on error
          if (field === 'pricePerTonne') {
            partner.pricePerTonne = previousValue as number | null;
          } else if (field === 'isDefault') {
            partner.isDefault = previousValue as boolean;
          }
          this.notificationService.error('Failed to update company configuration');
        },
      });
  }

}
